import type { Element } from 'react';
import React, { useEffect, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import moment from 'moment';
import { themes } from './Countdown.themes';
export type CountdownProps = {
  bgColor?: Colors;
  className?: string;
  color?: Colors;
  dataTestId?: string;
  dateTimeEnd: any;
  days?: string;
  description?: string;
  expiredRender?: boolean | string | Element<any>;
  hours?: string;
  minutes?: string;
  monthsLimitation?: number;
  roundToUnit?: 'days' | 'hours' | 'minutes' | 'seconds';
  seconds?: string;
  showTimer?: boolean;
  size?: string;
  theme?: keyof typeof themes;
  units?: Array<'days' | 'hours' | 'minutes' | 'seconds'>;
  updateFrequency?: number;
};

function calculateTimeLeft(dateTimeEnd): moment$MomentDuration {
  const now = moment();
  return now.isBefore(dateTimeEnd)
    ? moment.duration(moment(dateTimeEnd).diff(now))
    : moment.duration(0);
}

function resolveTime(unit, roundToUnit, timeRemaining) {
  if (roundToUnit === unit) {
    switch (roundToUnit) {
      case 'days':
        return Math.floor(timeRemaining.asDays()).toString();

      case 'hours':
        return Math.floor(timeRemaining.asHours()).toString();

      case 'minutes':
        return Math.floor(timeRemaining.asMinutes()).toString();

      case 'seconds':
        return Math.floor(timeRemaining.asSeconds()).toString();

      default:
        return Math.floor(timeRemaining.asSeconds()).toString();
    }
  }

  return timeRemaining.get(unit).toString();
}

export const Countdown = ({
  bgColor,
  className,
  color,
  dataTestId,
  dateTimeEnd,
  description,
  expiredRender,
  monthsLimitation = 0,
  roundToUnit = 'days',
  showTimer = true,
  size = 'large',
  theme = 'default',
  units = ['days', 'hours', 'minutes', 'seconds'],
  updateFrequency = 300,
  ...props
}: CountdownProps) => {
  const [timeRemaining, setTimeRemaining] = useState<moment$MomentDuration>(
    calculateTimeLeft(dateTimeEnd),
  );
  useEffect(() => {
    const timer = setTimeout(() => {
      if (dateTimeEnd && timeRemaining.asSeconds() > 0) {
        setTimeRemaining(calculateTimeLeft(dateTimeEnd));
      }
    }, updateFrequency);
    return () => clearTimeout(timer);
  }, [timeRemaining, dateTimeEnd]);
  const shouldDisplay = timeRemaining.asSeconds() > 0 && timeRemaining.months() <= monthsLimitation;

  if (!shouldDisplay) {
    if (dateTimeEnd && !!expiredRender) {
      return expiredRender;
    }

    return null;
  }

  if (!showTimer) {
    return null;
  }

  const { thItem, thLabel, thRoot, thValue, thDescription, thValuesWrapper } = themes[theme]({
    color,
    size,
    bgColor,
  });
  return (
    <div className={classnames(thRoot, className)} role="timer" data-testid={dataTestId}>
      {description && <div className={thDescription}>{description}</div>}
      <div className={thValuesWrapper}>
        {units.map((unit) => {
          const currentTime = resolveTime(unit, roundToUnit, timeRemaining);
          const transformedCurrentTime = currentTime.padStart(2, '0');
          return (
            <div className={thItem} key={unit}>
              <div className={thValue}>{transformedCurrentTime}</div>
              <div className={thLabel}> {props[unit]}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
